import { useState, useEffect } from 'react';

import { useAuth } from '#globalContexts/AuthProvider';

import { useCreditsWallet } from '../models/credits/queries';
import { useCreditsExhaustedStore } from '../stores/creditsExhaustedStore';

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const SEVEN_DAYS_IN_MS = 7 * ONE_DAY_IN_MS;

export const useCreditsExhaustedCheck = () => {
	const [showModal, setShowModal] = useState(false);
	const { data: wallet, isLoading: walletLoading } = useCreditsWallet();
	const { user, orgPlan } = useAuth();
	const { shouldShowModal, dismiss } = useCreditsExhaustedStore();

	// Users without an active paid subscription (demo or cancelled) can't get a credit refill,
	// so they're prompted to subscribe rather than told to wait for the next billing period.
	const needsSubscription = orgPlan === 'demo' || orgPlan === 'cancelled';

	// A pause suspends refills by design; the modal's "next billing period" copy would mislead.
	const isPaused = orgPlan === 'paused';

	useEffect(() => {
		if (isPaused || walletLoading || !wallet || !user?.id) return;

		const companyCredits = wallet.companyCredits || 0;
		const contactCredits = wallet.contactCredits || 0;
		const creditsExhausted = companyCredits <= 0 || contactCredits <= 0;

		if (!creditsExhausted) {
			if (showModal) setShowModal(false);
			return;
		}

		const throttleMs = needsSubscription ? ONE_DAY_IN_MS : SEVEN_DAYS_IN_MS;

		if (shouldShowModal(user.id, throttleMs)) {
			setShowModal(true);
		}
	}, [wallet, walletLoading, user?.id, needsSubscription, isPaused, shouldShowModal, showModal]);

	const handleCloseModal = () => {
		if (user?.id) {
			dismiss(user.id);
		}
		setShowModal(false);
	};

	return { showModal, handleCloseModal, needsSubscription };
};
